import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";

export type JsonRecord = Record<string, unknown>;

export type DesignerSchemaVersion = "reports-v3.1";
export type DesignerPageSize = "letter" | "legal";
export type DesignerPageOrientation = "portrait" | "landscape";

export type DesignerPageSettings = {
  size: DesignerPageSize;
  orientation: DesignerPageOrientation;
  margins?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
};

export type DesignerSelection =
  | { type: "report" }
  | { type: "band"; bandId: string }
  | { type: "component"; bandId: string; componentId: string }
  | { type: "field"; bandId: string; componentId: string; fieldId: string }
  | { type: "column"; bandId: string; componentId: string; columnId: string }
  | { type: "nativeElement"; bandId: string; elementIds: string[] }
  | { type: "nativeTableColumn"; bandId: string; tableId: string; columnId: string }
  | { type: "nativeTableGroup"; bandId: string; tableId: string; region?: "group" | "header" | "detail" | "footer" | "tableFooter" };

export type DesignerField = {
  id: string;
  legacyId?: string;
  label?: string;
  key?: string;
  format?: string;
  horizontalAlign?: string;
  styleId?: string;
  column?: number;
  row?: number;
  marginTop?: number;
  renderer: JsonRecord;
};

export type DesignerColumn = {
  id: string;
  legacyId?: string;
  label?: string;
  key?: string;
  format?: string;
  horizontalAlign?: string;
  width?: number;
  renderer: JsonRecord;
};

export type DesignerPositionedElement = {
  id: string;
  type: "text" | "field" | "container" | "table" | "image";
  x: number;
  y: number;
  width: number;
  height: number;
  text?: string;
  binding?: string;
  asset?: string;
  fit?: "contain" | "cover" | "stretch";
  lockAspectRatio?: boolean;
  dataset?: string;
  format?: string;
  style?: {
    fontSize?: number;
    bold?: boolean;
    italic?: boolean;
    color?: string;
    backgroundColor?: string;
    horizontalAlign?: "left" | "center" | "right";
    verticalAlign?: "top" | "middle" | "bottom";
    lineHeight?: number;
    borderColor?: string;
    borderWidth?: number;
  };
  padding?: number | { top?: number; right?: number; bottom?: number; left?: number };
  gap?: number;
  layout?: JsonRecord;
  elements?: DesignerPositionedElement[];
  columns?: Array<{
    id: string;
    key?: string;
    binding?: string;
    expr?: JsonRecord;
    calculation?: JsonRecord;
    label?: string;
    width?: number | string;
    visible?: boolean;
    hidden?: boolean;
    format?: string;
    horizontalAlign?: string;
    headerHorizontalAlign?: string;
    style?: JsonRecord;
  }>;
  header?: JsonRecord;
  row?: JsonRecord;
  footer?: JsonRecord;
  groups?: JsonRecord[];
  sort?: JsonRecord[];
  groupHeader?: JsonRecord;
  groupFooter?: JsonRecord;
  pagination?: JsonRecord;
  cellPadding?: number | { top?: number; right?: number; bottom?: number; left?: number };
  emptyText?: string;
  renderer: JsonRecord;
};

export type DesignerComponent =
  | {
      id: string;
      kind: "keyValueGrid";
      datasetId?: string;
      styleId?: string;
      outputs: string[];
      fields: DesignerField[];
      renderer: JsonRecord;
    }
  | {
      id: string;
      kind: "table";
      datasetId?: string;
      styleId?: string;
      outputs: string[];
      columns: DesignerColumn[];
      renderer: JsonRecord;
    }
  | {
      id: string;
      kind: "text";
      styleId?: string;
      outputs: string[];
      content?: string;
      renderer: JsonRecord;
    }
  | {
      id: string;
      kind: "rendererSection";
      legacyType: string;
      datasetId?: string;
      styleId?: string;
      outputs: string[];
      renderer: JsonRecord;
    };

export type DesignerBand = {
  id: string;
  legacyId?: string;
  role: "reportHeader" | "pageHeader" | "groupHeader" | "details" | "groupFooter" | "reportFooter" | "pageFooter" | "custom";
  title: string;
  height?: number;
  positionedElements?: DesignerPositionedElement[];
  componentIds: string[];
  components: DesignerComponent[];
  renderer: JsonRecord;
};

export type DesignerDocument = {
  schemaVersion: DesignerSchemaVersion;
  sourceSchemaVersion?: string;
  definitionId?: string | null;
  definitionVersion?: unknown;
  reportKey?: string;
  reportType?: string;
  scope?: string;
  overrideOf?: string;
  name?: string;
  roles?: unknown[];
  requiredFilters?: JsonRecord;
  capabilities?: JsonRecord;
  data?: unknown;
  datasetContract?: JsonRecord;
  output?: unknown;
  metadata: JsonRecord;
  assets: JsonRecord;
  datasets: JsonRecord;
  page: DesignerPageSettings;
  styles: JsonRecord;
  outputs: JsonRecord;
  bands: DesignerBand[];
  renderer: {
    topLevel: JsonRecord;
    layout: JsonRecord;
  };
};

export type DesignerHistoryState = {
  past: DesignerDocument[];
  present: DesignerDocument;
  future: DesignerDocument[];
  dirty: boolean;
};

export function cloneDesignerDocument(document: DesignerDocument): DesignerDocument {
  return cloneValue(document);
}

export function clonePayload(payload: FormattedInvoicePayload): FormattedInvoicePayload {
  return cloneValue(payload);
}

export function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function asRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? value as JsonRecord : {};
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function cleanRecord(record: JsonRecord): JsonRecord {
  return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));
}

export function normalizePageSize(value: unknown): DesignerPageSize {
  return String(value || "").toLowerCase() === "legal" ? "legal" : "letter";
}

export function normalizeOrientation(value: unknown): DesignerPageOrientation {
  return String(value || "").toLowerCase() === "landscape" ? "landscape" : "portrait";
}

export function denormalizePageSize(value: DesignerPageSize): string {
  return value === "legal" ? "Legal" : "Letter";
}

export function findBand(document: DesignerDocument, bandId: string): DesignerBand | undefined {
  return document.bands.find((band) => band.id === bandId);
}

export function findComponent(document: DesignerDocument, bandId: string, componentId: string): DesignerComponent | undefined {
  return findBand(document, bandId)?.components.find((component) => component.id === componentId);
}

export function primaryComponent(band: DesignerBand | undefined): DesignerComponent | undefined {
  if (!band) return undefined;
  return band.components.find((component) => band.componentIds.includes(component.id)) || band.components[0];
}
